import type { FC } from 'react';
import React from 'react';
import { ScreenName } from '@helpcenter/constants';
import ExternalResourceIcon from '@helpcenter/external-resource-fallback-icon';
import { graphql, useFragment } from 'react-relay';
import { NewTabTooltipContainer } from 'topics/src/common/ui/new-tab-tooltip-container';
import { GroupBoxLink } from 'view/common/group-box';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import BookIcon from '@atlaskit/icon/core/book-with-bookmark';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { AnalyticsScreen } from '@atlassian/help-center-common-util/analytics/analytics-screen';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { addSourceToUrl } from '@atlassian/help-center-common-util/article-util';
import { isExternalResource, urlRefsAndTarget } from '@atlassian/help-center-common-util/is-external-resource';
import { getUpdatedDisplayLink } from '@atlassian/help-center-common-util/url';
import type { Props } from './types';

const HelpObjectArticleComponent: FC<Props> = (props: Props) => {
    const data = useFragment<Props['helpObject']>(
        graphql`
            fragment helpObjectArticle_HelpObjectStoreArticle on HelpObjectStoreArticle {
                title
                description
                displayLink
                entityKey
                id
            }
        `,
        props.helpObject
    );
    const { title, description, entityKey } = data;
    if (title?.length && typeof data.displayLink === 'string' && data.displayLink) {
        const isAnExternalResource = isExternalResource(entityKey as string);
        const linkProps = urlRefsAndTarget({
            isAnExternalResource,
            displayLink: addSourceToUrl(getUpdatedDisplayLink(data.displayLink), 'topic', isAnExternalResource),
        });

        const attributes = {
            id: data.id,
            displayLink: data.displayLink,
            entityKey: data.entityKey,
        };
        return (
            <AnalyticsScreen screenName={ScreenName.TOPIC_ITEM_HOMEPAGE}>
                <AnalyticsContext data={{ attributes }}>
                    <NewTabTooltipContainer shouldOpenInANewTab={isAnExternalResource}>
                        <GroupBoxLink
                            {...linkProps}
                            icon={
                                isAnExternalResource ? (
                                    <ExternalResourceIcon
                                        iconUrl={getUpdatedDisplayLink(data.displayLink)}
                                        resourceType="ARTICLE"
                                        size="large"
                                    />
                                ) : (
                                    <BookIcon
                                        color={token('color.icon', colors.N500)}
                                        label="helpObjectArticle"
                                        size="medium"
                                        spacing="spacious"
                                    />
                                )
                            }
                            heading={title}
                            description={description}
                            actionSubjectId="helpObject"
                        ></GroupBoxLink>
                    </NewTabTooltipContainer>
                    <FireScreenEventOnMount />
                </AnalyticsContext>
            </AnalyticsScreen>
        );
    }
    return null;
};

export default HelpObjectArticleComponent;
